<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema, $ } from '@bagelink/vue'
const schema = defineSchema({
	title: $.text('Title', { default: 'All Authors' }),
})
export const BlockAllAuthors = defineBlock({
	thumbnail: '/block-thumbs/BlockAllAuthors.svg',
	category: 'Listings',
	order: 80,
	name: 'BlockAllAuthors',
	label: 'All Authors',
	icon: 'groups',
	schema,
	dataSource: { collection: 'authors', store: 'the_torah', label: 'Authors' },
})
</script>
<script setup lang="ts">
import { useBloxRuntime } from '@bagelink/blox'
import { Btn } from '@bagelink/vue'
import { computed, ref } from 'vue'
import AuthorCard from '@/components/blocks/AuthorCard.vue'
import AuthorPanoramic from '@/components/blocks/AuthorPanoramic.vue'
import { useCollection } from '@/composables/useDatastore'



interface Author {
	name: string
	lastName: string
	title: string
	image: string
	bio: string
	link: string
	hidden?: boolean
}

defineProps<{
	title?: string
}>()

const selectedFilter = ref('ALL')
const isPanoramic = ref(false)
const PREVIEW_AUTHORS: Author[] = [
	{
		name: 'Sarah Cohen',
		lastName: 'Cohen',
		title: 'Dr.',
		image: '/webclip.png',
		bio: 'A sample scholar profile shown only in the page designer.',
		link: '/',
	},
	{
		name: 'David Levi',
		lastName: 'Levi',
		title: 'Prof.',
		image: '/og.jpg',
		bio: 'A second sample profile for previewing the authors layout.',
		link: '/',
	},
]

const { isPreview } = useBloxRuntime()
const { items: datastoreAuthors, loading } = useCollection('authors', {
	all: true,
	limit: 1000,
	pick: ['id', '_id', 'name', 'lastName', 'title', 'image', 'bio', 'slug', 'hidden'],
})

function lastNameOf(author: { name: string; lastName?: string }) {
	const lastName = author.lastName?.trim()
	if (lastName) return lastName
	return author.name.trim().split(/\s+/).pop() || author.name
}

const authors = computed<Author[]>(() => {
	const mapped = datastoreAuthors.value
		.filter((author: any) => !author.hidden)
		.map((author: any) => ({
			name: author.name || '',
			lastName: author.lastName || '',
			title: author.title || '',
			image: author.image || '',
			bio: author.bio || '',
			link: `/author/${author.slug}`,
		}))
		.sort(
			(a: Author, b: Author) =>
				lastNameOf(a).localeCompare(lastNameOf(b), 'en', { sensitivity: 'base' }) ||
				a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }),
		)
	if (mapped.length || loading.value || !isPreview.value) return mapped
	return PREVIEW_AUTHORS
})
const filters = ['ALL', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'].map(letter => ({ letter }))

const activeLetters = computed(() => {
	const letters = new Set<string>()
	for (const author of authors.value) {
		const initial = lastNameOf(author).charAt(0).toUpperCase()
		if (initial) letters.add(initial)
	}
	return letters
})

function isFilterAvailable(letter: string) {
	return letter === 'ALL' || activeLetters.value.has(letter)
}

function toggleView() {
	isPanoramic.value = !isPanoramic.value
}

function shuffled<T>(items: T[]) {
	const copy = [...items]
	for (let index = copy.length - 1; index > 0; index -= 1) {
		const swap = Math.floor(Math.random() * (index + 1))
		;[copy[index], copy[swap]] = [copy[swap], copy[index]]
	}
	return copy
}

const panoramicAuthors = computed(() => shuffled(authors.value))

function filterAuthors() {
	const allAuthors = authors.value
	// The panoramic view is always a single, unfiltered wall of authors —
	// the letters only belong to the alphabetical card view.
	if (isPanoramic.value || selectedFilter.value === 'ALL') {
		return allAuthors
	}
	return allAuthors.filter(
		author => lastNameOf(author).charAt(0).toUpperCase() === selectedFilter.value,
	)
}
</script>

<template>
	<div class="mx-auto pt-2-5 pb-1-5 txt-center m_pt-2 m_pb-1 pageHeight">
		<h1 class="my-0 txt40 regular line-height-14 m_txt32 pb-1">
			{{ title }}
		</h1>
		<div class="flex gap-1 justify-content-center m_column m_gap-075 m_pt-2">
			<p v-if="!isPanoramic" class="txt17">Browse authors by last name</p>
			<Btn flat thin class="color-primary txt17" @click="toggleView()">
				{{ isPanoramic ? 'Browse authors by name' : 'A panoramic view' }}
			</Btn>
		</div>
		<div
			v-if="!isPanoramic"
			class="mx-auto txt-center overflow w-max-100vw alphabetWrap pt-1-75 m_pt-2-5 pb-1 sticky-3 bg-white z-4 m_ps-1"
		>
			<div class="flex justify-content-center m_justify-content-start gap-025 בםךם">
				<Btn
					v-for="filter in filters"
					:key="filter.letter"
					thin
					flat
					:class="{
						'color-primary': selectedFilter === filter.letter && isFilterAvailable(filter.letter),
						'pointer-events-none opacity-3': !isFilterAvailable(filter.letter),
					}"
					class="txt18 px-05 light flex-shrink-0 letter-btn"
					@click="selectedFilter = filter.letter"
				>
					{{ filter.letter }}
				</Btn>
			</div>
		</div>

		<div
			v-if="isPanoramic"
			class="w80p author-panoramic-wrap flex flex-wrap mx-auto pt-2 justify-content-center m_px-0 m_gap-025 m_w90p"
		>
			<AuthorPanoramic v-for="author in panoramicAuthors" :key="author.link || author.name" :author="author" />
		</div>

		<div v-else class="w720px mx-auto pt-2 m_px-1">
			<AuthorCard
				v-for="author in filterAuthors()"
				:key="author.name"
				:bold-name="true"
				:author="author"
				:rows="2"
				:mobile-rows="3"
			/>
		</div>
	</div>
</template>

<style>
.alphabetWrap {
	max-width: 100vw;
}
.letter-btn:hover {
	color: var(--bgl-primary);
}
</style>
